import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { addClearPatientFee, removeClearPatientFee } from "helpers/fee";
import { centsToDollars, parsePrice } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { useOrder } from "hooks/useOrder";
import usePrice from "hooks/usePrice";
import { Label } from "layout/typography/Label";
import { sumBy } from "lodash";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useMemo, useEffect, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IOrder } from "types/Order/Order";

const DOWNPAYMENT_ONLY_TYPE_OPTIONS = ["Percentage", "Flat amount"];

export function DownPaymentOnlySection({
  editing,
  setDownpaymentOnlyFieldsValid,
  order,
  orderStatus,
}: {
  editing: boolean;
  setDownpaymentOnlyFieldsValid: (value: boolean) => void;
  order: IOrder;
  orderStatus: string;
}) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { getClearPriceByOrderType } = usePrice();
  const { isDefaultOrderType } = useOrder(order);
  const downpaymentOnlyTypeField = watch("downpaymentOnlyType");
  const downpaymentOnlyPercentField = watch("downpaymentOnlyPercent");
  const downpaymentOnlyFlatAmountField = watch("downpaymentOnlyFlatAmount");

  const showPercentageField = useMemo(() => {
    return downpaymentOnlyTypeField === "Percentage";
  }, [downpaymentOnlyTypeField]);

  const handleChangeDownpaymentOnlyType = (e: { value: string }) => {
    setValue("downpaymentOnlyType", e.value);
    if (e.value === "Percentage") {
      setValue("downpaymentOnlyFlatAmount", null);
    } else {
      setValue("downpaymentOnlyPercent", null);
    }
  };

  useEffect(() => {
    const validateFields = async () => {
      const isValid = await trigger();
      setDownpaymentOnlyFieldsValid(isValid);
    };
    validateFields();
  }, [
    downpaymentOnlyTypeField,
    downpaymentOnlyPercentField,
    downpaymentOnlyFlatAmountField,
  ]);

  const paymentInfo = useMemo(() => {
    const { total, clearPrice } = getClearPriceByOrderType({ order });

    const subtotalValue = sumBy(
      order.serviceDetails.procedures as any,
      "amountInCents"
    );

    const subTotal = isDefaultOrderType ? subtotalValue : total;
    const totalWithDiscount = clearPrice;

    // We calculate Clear Patient Fee using the percentage saved in the order table
    let clearPatientFee = 0;
    let clearPatientFeeInDollars = 0;

    // For GFE orders, patientResponsibilityEstimateAmountInCents now includes the Clear Patient Fee, when it exists
    // So we need to calculate the subtotal by subtracting the Clear Patient Fee
    if (
      order.orderType === orderTypeConstants.GFE &&
      order.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0
    ) {
      // Convert to dollars, remove fee with rounding logic, convert back
      const totalInDollars = centsToDollars(total);
      const subtotalInDollars = removeClearPatientFee(
        totalInDollars,
        order.clearPatientFeeValueInPercentage
      );
      const subtotalAmount = subtotalInDollars * 100; // back to cents for internal calc
      clearPatientFee = total - subtotalAmount;
      clearPatientFeeInDollars = centsToDollars(clearPatientFee);
    }

    // For GFE orders, calculate the display values
    let rawTotal;
    let rawTotalWithPatientFee;

    if (order.orderType === orderTypeConstants.GFE && clearPatientFee > 0) {
      // total field contains the total including Clear Patient Fee
      rawTotalWithPatientFee = centsToDollars(total);
      // Calculate subtotal by subtracting Clear Patient Fee
      rawTotal = centsToDollars(total - clearPatientFee);
    } else {
      // For non-GFE orders or GFE orders without Clear Patient Fee
      rawTotal = centsToDollars(subTotal);
      // rawTotalWithPatientFee not needed since we don't display it in this case
    }

    const rawTotalWithDiscount = centsToDollars(totalWithDiscount);

    return {
      rawTotalWithDiscount,
      rawTotal,
      rawTotalWithPatientFee,
      clearPatientFeeInDollars,
    };
  }, [order, order.serviceDetails, order.serviceDetails.procedures]);

  // Max value for stored value (with fees for GFE with clear patient fee)
  const flatAmountMax = useMemo(() => {
    if (
      order.orderType === orderTypeConstants.GFE &&
      paymentInfo.clearPatientFeeInDollars > 0
    ) {
      return paymentInfo.rawTotalWithPatientFee;
    }
    if (order.orderType === orderTypeConstants.GFE) {
      return paymentInfo.rawTotal;
    }
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return paymentInfo.rawTotal;
    }
    return paymentInfo.rawTotalWithDiscount;
  }, [paymentInfo, order.orderType]);

  // Max value for display (without fees) - used for InputNumber max prop
  const flatAmountMaxDisplay = useMemo(() => {
    if (order.orderType === orderTypeConstants.GFE) {
      return paymentInfo.rawTotal;
    }
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return paymentInfo.rawTotal;
    }
    return paymentInfo.rawTotalWithDiscount;
  }, [paymentInfo, order.orderType]);

  // Calculate display value for flat amount without clear patient fee, when it exists
  const flatAmountDisplayValue = useMemo(() => {
    return downpaymentOnlyFlatAmountField &&
      order?.clearPatientFeeValueInPercentage &&
      order.orderType === orderTypeConstants.GFE
      ? removeClearPatientFee(
          downpaymentOnlyFlatAmountField,
          order.clearPatientFeeValueInPercentage
        )
      : downpaymentOnlyFlatAmountField;
  }, [
    downpaymentOnlyFlatAmountField,
    order?.clearPatientFeeValueInPercentage,
    order.orderType,
  ]);

  // Validation function for flat amount that checks stored value against max
  const validateFlatAmount = useCallback(
    (value: number | null | undefined) => {
      if (!value) {
        return true; // Let required validation handle empty values
      }

      if (orderStatus === orderStatusConstants.PAID) {
        return true;
      }

      const maxValue = flatAmountMax ?? 0;
      if (value > maxValue) {
        // Show the display max (without fees) in the error message
        return `Amount cannot exceed ${parsePrice(flatAmountMaxDisplay ?? 0)}`;
      }

      return true;
    },
    [flatAmountMax, flatAmountMaxDisplay, orderStatus]
  );

  return (
    <div className="flex flex-column gap-4 w-full">
      <div className="formgrid grid w-full">
        <div className="field flex flex-column col-12 lg:col-4 xl:col-3">
          <Label
            htmlFor="downpaymentOnlyType"
            data-testid="downpaymentOnlyTypeLabel"
          >
            Downpayment Only Type
          </Label>
          <Controller
            name="downpaymentOnlyType"
            control={control}
            defaultValue="Percentage"
            render={({ field: { value } }) => (
              <Dropdown
                id="downpaymentOnlyType"
                data-testid="downpaymentOnlyType_Dropdown"
                value={value}
                onChange={handleChangeDownpaymentOnlyType}
                options={DOWNPAYMENT_ONLY_TYPE_OPTIONS}
                placeholder="Select downpayment only type"
                className="w-full"
                disabled={!editing || orderStatus === orderStatusConstants.PAID}
              />
            )}
          />
        </div>
        <div
          className={`field flex-column col-12 lg:col-4 xl:col-3 ${
            showPercentageField ? "flex" : "hidden"
          }`}
        >
          <Label
            htmlFor="downpaymentOnlyPercent"
            data-testid="downpaymentOnlyPercentLabel"
          >
            Downpayment Only Percent
          </Label>
          <Controller
            name="downpaymentOnlyPercent"
            control={control}
            rules={{
              required: showPercentageField
                ? "Downpayment only percent is required"
                : false,
              min: {
                value: 1,
                message: "Percent must be at least 1%",
              },
              max: {
                value: 100,
                message: "Percent cannot exceed 100%",
              },
            }}
            render={({ field: { onChange, value, onBlur } }) => (
              <>
                <InputNumber
                  value={value}
                  suffix="%"
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "downpaymentOnlyPercent",
                      trigger,
                    })
                  }
                  inputId="downpaymentOnlyPercent"
                  data-testid="downpaymentOnlyPercent_Input"
                  onValueChange={onChange}
                  min={1}
                  max={100}
                  placeholder="Enter percentage"
                  disabled={
                    !editing || orderStatus === orderStatusConstants.PAID
                  }
                />
                {getFormErrorMessage("downpaymentOnlyPercent", errors)}
              </>
            )}
          />
        </div>
        <div
          className={`field flex-column col-12 lg:col-4 xl:col-3 ${
            showPercentageField ? "hidden" : "flex"
          }`}
        >
          <Label
            htmlFor="downpaymentOnlyFlatAmount"
            data-testid="downpaymentOnlyFlatAmountLabel"
          >
            Amount
          </Label>
          <Controller
            name="downpaymentOnlyFlatAmount"
            control={control}
            rules={{
              required: !showPercentageField ? "Amount is required" : false,
              min: {
                value: 1,
                message: "Amount must be at least $1",
              },
              validate: validateFlatAmount,
            }}
            render={({ field: { onChange, value, onBlur } }) => (
              <>
                <InputNumber
                  value={flatAmountDisplayValue}
                  currency="USD"
                  locale="en-US"
                  mode="currency"
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "downpaymentOnlyFlatAmount",
                      trigger,
                    })
                  }
                  inputId="downpaymentOnlyFlatAmount"
                  data-testid="downpaymentOnlyFlatAmount_Input"
                  onValueChange={(e) => {
                    // When user changes value, calculate total with fees for storage
                    const newValue = e.value;
                    const valueToStore =
                      newValue &&
                      order?.clearPatientFeeValueInPercentage &&
                      order.orderType === orderTypeConstants.GFE
                        ? addClearPatientFee(
                            newValue,
                            order.clearPatientFeeValueInPercentage
                          )
                        : newValue;
                    onChange(valueToStore);
                  }}
                  min={1}
                  max={flatAmountMaxDisplay ?? 0}
                  placeholder="Enter amount"
                  disabled={
                    !editing || orderStatus === orderStatusConstants.PAID
                  }
                />
                {value &&
                  order?.clearPatientFeeValueInPercentage &&
                  order.orderType === orderTypeConstants.GFE && (
                    <div className="mt-2">
                      <p className="text-sm mb-1 text-primary">
                        with fees: {parsePrice(value)}
                      </p>
                    </div>
                  )}
                {getFormErrorMessage("downpaymentOnlyFlatAmount", errors)}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}
